import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useCart } from '../../context/CartContext';
import { useLanguage } from '../../context/LanguageContext';
import './header.css';

const NAV_LINKS = [
  { labelKey: 'nav.home', to: '/' },
  { labelKey: 'nav.shop', to: '/shop' },
  { labelKey: 'nav.categories', to: '/#home-categories' },
  { labelKey: 'nav.promotions', to: '/#home-promotions' },
  { labelKey: 'nav.about', to: '/about-us' },
  { labelKey: 'nav.contact', to: '/contact-us' },
];

const Header = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { itemCount } = useCart();
  const { language, setLanguage, t } = useLanguage();
  const [menuActive, setMenuActive] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const currentSearch = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get('search') || '';
  }, [location.search]);

  useEffect(() => {
    setMenuActive(false);
  }, [location.pathname]);

  useEffect(() => {
    setSearchTerm(currentSearch);
  }, [currentSearch]);

  const isSelected = (path) => {
    if (path.includes('#')) {
      const [pathname, hash] = path.split('#');
      return location.pathname === pathname && location.hash === `#${hash}`;
    }

    if (location.hash && path === '/') {
      return false;
    }

    return location.pathname === path;
  };

  const handleNavClick = (target) => {
    if (!target.includes('#')) {
      setMenuActive(false);
      return;
    }

    navigate(target);
    setMenuActive(false);
  };

  const handleSearchSubmit = (event) => {
    event.preventDefault();

    const trimmedSearch = searchTerm.trim();
    const currentParams = new URLSearchParams(location.search);
    const nextParams = new URLSearchParams();
    const activeCategory = currentParams.get('category');

    if (activeCategory) {
      nextParams.set('category', activeCategory);
    }

    if (trimmedSearch) {
      nextParams.set('search', trimmedSearch);
    }

    navigate(`/shop${nextParams.toString() ? `?${nextParams.toString()}` : ''}`);
    setMenuActive(false);
  };

  return (
    <header className="site-header">
      <div className="header-inner">
        <Link to="/" className="brand-link" aria-label="Mobiluxe home">
          <img src="/images/mobiluxe-wordmark-transparent.png" alt="Mobiluxe Logo" />
        </Link>

        <nav className="desktop-nav" aria-label="Navigation principale">
          {NAV_LINKS.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className={isSelected(item.to) ? 'selected' : ''}
              onClick={() => handleNavClick(item.to)}
            >
              {t(item.labelKey)}
            </Link>
          ))}
        </nav>

        <form className="desktop-search" onSubmit={handleSearchSubmit}>
          <i className="fas fa-search" aria-hidden="true" />
          <input
            type="search"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            placeholder={t('nav.search')}
            aria-label={t('nav.search')}
          />
          <button type="submit" aria-label={t('nav.search')}>
            <i className="fas fa-arrow-right" aria-hidden="true" />
          </button>
        </form>

        <div className="header-actions">
          <Link
            to="/buy"
            className={`header-cart-link${isSelected('/buy') ? ' selected' : ''}`}
            aria-label={itemCount > 0 ? `${t('nav.cart')}, ${itemCount} ${t('common.products')}` : t('nav.cart')}
          >
            <span className="header-cart-icon">
              <i className="fas fa-shopping-cart" aria-hidden="true" />
              {itemCount > 0 && <span className="nav-cart-count">{itemCount}</span>}
            </span>
            <span className="header-cart-copy">
              <span className="header-cart-label">{t('nav.cart')}</span>
            </span>
            <span className="header-cart-arrow" aria-hidden="true">
              <i className="fas fa-angle-right" />
            </span>
          </Link>

          <Link to="/#home-categories" className="header-cta" onClick={() => handleNavClick('/#home-categories')}>
            <span>{t('nav.checkout')}</span>
            <i className="fas fa-arrow-right" aria-hidden="true" />
          </Link>
        </div>

        <div className="language-switcher desktop-language-switcher" role="group" aria-label={t('language.label')}>
          <button type="button" className={language === 'fr' ? 'active' : ''} aria-pressed={language === 'fr'} onClick={() => setLanguage('fr')}>FR</button>
          <button type="button" className={language === 'ar' ? 'active' : ''} aria-pressed={language === 'ar'} onClick={() => setLanguage('ar')}>{t('language.arabic')}</button>
        </div>

        <div className="mobile-actions">
          <Link
            to="/buy"
            className="mobile-cart-link"
            aria-label={itemCount > 0 ? `${t('nav.cart')}, ${itemCount} ${t('common.products')}` : t('nav.cart')}
          >
            <i className="fas fa-shopping-cart" aria-hidden="true" />
            {itemCount > 0 && <span className="nav-cart-count">{itemCount}</span>}
          </Link>

          <button
            type="button"
            className="menu-toggle"
            aria-label={menuActive ? t('nav.closeMenu') : t('nav.openMenu')}
            aria-expanded={menuActive}
            onClick={() => setMenuActive((prev) => !prev)}
          >
            <i
              className={menuActive ? 'fas fa-times menu-toggle-icon' : 'fas fa-bars menu-toggle-icon'}
              aria-hidden="true"
            />
          </button>
        </div>

        <nav className={`primary-nav ${menuActive ? 'active' : ''}`} aria-label="Navigation mobile">
          <form className="mobile-search" onSubmit={handleSearchSubmit}>
            <i className="fas fa-search" aria-hidden="true" />
            <input
              type="search"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder={t('nav.search')}
              aria-label={t('nav.search')}
            />
            <button type="submit">OK</button>
          </form>

          <ul>
            {NAV_LINKS.map((item) => (
              <li key={item.to}>
                <Link
                  to={item.to}
                  className={isSelected(item.to) ? 'selected' : ''}
                  onClick={() => handleNavClick(item.to)}
                >
                  {t(item.labelKey)}
                </Link>
              </li>
            ))}
            <li className="mobile-language-switcher">
              <div className="language-switcher" role="group" aria-label={t('language.label')}>
                <button type="button" className={language === 'fr' ? 'active' : ''} aria-pressed={language === 'fr'} onClick={() => setLanguage('fr')}>FR</button>
                <button type="button" className={language === 'ar' ? 'active' : ''} aria-pressed={language === 'ar'} onClick={() => setLanguage('ar')}>{t('language.arabic')}</button>
              </div>
            </li>
            <li className="nav-cart">
              <Link
                to="/buy"
                className={isSelected('/buy') ? 'selected' : ''}
                aria-label={itemCount > 0 ? `${t('nav.cart')}, ${itemCount} ${t('common.products')}` : t('nav.cart')}
              >
                <i className="fas fa-shopping-cart" aria-hidden="true" />
                <span className="nav-cart-label">{t('nav.cart')}</span>
              </Link>
            </li>
            <li className="nav-cta">
              <Link to="/#home-categories" onClick={() => handleNavClick('/#home-categories')}>
                <span>{t('nav.checkout')}</span>
                <i className="fas fa-arrow-right" aria-hidden="true" />
              </Link>
            </li>
          </ul>
        </nav>
      </div>
    </header>
  );
};

export default Header;
